/**
 * Tender Quote Sections API Service
 * 
 * Provides standardized methods for working with tender quote sections
 */
import api from './api';

/**
 * Tender Quote Sections service with standardized methods
 */
export const tenderQuoteSectionService = {
  /**
   * Find the corresponding TenderQuote ID for a Quote that's part of a Tender
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @returns {Promise<number|null>} Found tender quote ID or null if not found
   */
  async findTenderQuoteId(tenderId, quoteId) {
    
    // Use the standardized API client to fetch tender details
    try {
      // Get tender with quotes included
      const tender = await api.get(`tenders/${tenderId}`);
      
      if (!tender || !tender.quotes || !Array.isArray(tender.quotes)) {
        return null;
      }
      
      // Find the tender quote that matches our quote ID
      const tenderQuote = tender.quotes.find((tq) => 
        Number(tq.quote_id) === Number(quoteId),
      );
      
      if (tenderQuote) {
        return tenderQuote.id;
      } else {
        return null;
      }
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Get sections for a tender quote
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} tenderQuoteId - Tender Quote ID
   * @returns {Promise<Object>} Sections data
   */
  async getSections(tenderId, tenderQuoteId) {

    try {
      // The URL structure is /tenders/{tenderId}/quotes/{tenderQuoteId}/sections
      // NOT /tenders/{tenderId}/quotes/{quoteId}/sections
      // tenderQuoteId is the ID from the tender_quotes join table, not the quote_id!
      const response = await api.get(`tenders/${tenderId}/quotes/${tenderQuoteId}/sections`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get a single section with its items
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} tenderQuoteId - Tender Quote ID
   * @param {number|string} sectionId - Section ID
   * @returns {Promise<Object>} Section data with items
   */
  async getSection(tenderId, tenderQuoteId, sectionId) {

    try {
      const response = await api.get(`tenders/${tenderId}/quotes/${tenderQuoteId}/sections/${sectionId}`);
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Add a new section to a tender quote
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} tenderQuoteId - Tender Quote ID
   * @param {Object} sectionData - Section data to add
   * @returns {Promise<Object>} Created section
   */
  async addSection(tenderId, tenderQuoteId, sectionData) {
    
    try {
      const response = await api.post(`tenders/${tenderId}/quotes/${tenderQuoteId}/sections`, sectionData);
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Update a section in a tender quote
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} tenderQuoteId - Tender Quote ID
   * @param {number|string} sectionId - Section ID
   * @param {Object} sectionData - Updated section data
   * @returns {Promise<Object>} Updated section
   */
  async updateSection(tenderId, tenderQuoteId, sectionId, sectionData) {
    
    try {
      const response = await api.put(
        `tenders/${tenderId}/quotes/${tenderQuoteId}/sections/${sectionId}`,
        sectionData,
      );
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Delete a section from a tender quote
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} tenderQuoteId - Tender Quote ID
   * @param {number|string} sectionId - Section ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteSection(tenderId, tenderQuoteId, sectionId) {
    
    try {
      const response = await api.delete(
        `tenders/${tenderId}/quotes/${tenderQuoteId}/sections/${sectionId}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Update regular section items (non-labor items)
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} tenderQuoteId - Tender Quote ID
   * @param {number|string} sectionId - Section ID
   * @param {Array} itemIds - Array of regular item IDs to assign
   * @returns {Promise<Object>} Update result
   */
  async updateSectionItems(tenderId, tenderQuoteId, sectionId, itemIds) {

    try {
      const response = await api.post(
        `tenders/${tenderId}/quotes/${tenderQuoteId}/sections/${sectionId}/items`,
        { item_ids: itemIds },  // Use item_ids to match backend validation
      );
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Update labor section items
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} tenderQuoteId - Tender Quote ID
   * @param {number|string} sectionId - Section ID
   * @param {Array} laborItemIds - Array of labor item IDs to assign
   * @returns {Promise<Object>} Update result
   */
  async updateSectionLaborItems(tenderId, tenderQuoteId, sectionId, laborItemIds) {
    //   tenderId, tenderQuoteId, sectionId, laborItemIds
    // });
    
    // Validate and filter labor item IDs
    const validIds = laborItemIds.filter((id) => {
      const numericId = parseInt(id, 10);
      if (isNaN(numericId) || numericId <= 0) {
        return false;
      }
      return true;
    }).map((id) => parseInt(id, 10));
    
    if (validIds.length === 0) {
      return { status: 'success', message: 'No labor items to assign' };
    }
    
    try {
      const response = await api.post(
        `tenders/${tenderId}/quotes/${tenderQuoteId}/sections/${sectionId}/labor`,
        { labor_item_ids: validIds },
      );
      return response;
    } catch (error) {
      
      // Enhanced error handling for specific error types
      if (error.response?.data?.message) {
        const errorMessage = error.response.data.message;
        if (errorMessage.includes('foreign key constraint') || errorMessage.includes('not valid')) {
          return {
            status: 'error',
            message: 'Some labor items are no longer available. Please refresh the page and try again.',
            error_type: 'validation_error',
          };
        }
      }
      
      throw error;
    }
  },
  
  /**
   * Update both regular and labor items for a section
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} tenderQuoteId - Tender Quote ID
   * @param {number|string} sectionId - Section ID
   * @param {Array} regularItemIds - Array of regular item IDs
   * @param {Array} laborItemIds - Array of labor item IDs
   * @returns {Promise<Object>} Update result with combined status
   */
  async updateAllSectionItems(tenderId, tenderQuoteId, sectionId, regularItemIds, laborItemIds) {
    
    const results = {
      regular: null,
      labor: null,
      status: 'success',
      errors: [],
    };
    
    try {
      // Update regular items if we have any
      if (regularItemIds.length > 0) {
        const regularResult = await this.updateSectionItems(
          tenderId, tenderQuoteId, sectionId, regularItemIds,
        );
        results.regular = regularResult;
        
        if (regularResult.status === 'error') {
          results.status = 'error';
          results.errors.push(`Regular items error: ${regularResult.message}`);
        }
      }
      
      // Update labor items if we have any
      if (laborItemIds.length > 0) {
        const laborResult = await this.updateSectionLaborItems(
          tenderId, tenderQuoteId, sectionId, laborItemIds,
        );
        results.labor = laborResult;
        
        if (laborResult.status === 'error') {
          results.status = 'error';
          results.errors.push(`Labor items error: ${laborResult.message}`);
        } else {
        }
      }
      
      return results;
    } catch (error) {
      results.status = 'error';
      results.errors.push(`API error: ${error.message}`);
      return results;
    }
  },
};

export default tenderQuoteSectionService;